import { useState, useCallback } from "react";

export const useTriggers = (initialTriggers = []) => {
  const [triggers, setTriggers] = useState(initialTriggers);

  const toggleTriggerAction = useCallback(
    (contentType, action) => {
      // Calculate the updated triggers first, then set state
      const calculateUpdatedTriggers = (prevTriggers) => {
        const existingIndex = prevTriggers.findIndex(
          (t) => t.type === contentType
        );

        let updatedTriggers;

        if (existingIndex === -1) {
          updatedTriggers = [
            ...prevTriggers,
            { type: contentType, functions: [action] },
          ];
        } else {
          const existing = prevTriggers[existingIndex];
          const functionSet = new Set(existing.functions);

          if (functionSet.has(action)) {
            functionSet.delete(action);
          } else {
            functionSet.add(action);
          }

          const updatedFunctions = Array.from(functionSet);

          if (updatedFunctions.length === 0) {
            updatedTriggers = prevTriggers.filter(
              (_, idx) => idx !== existingIndex
            );
          } else {
            updatedTriggers = prevTriggers.map((trigger, idx) =>
              idx === existingIndex
                ? { ...trigger, functions: updatedFunctions }
                : trigger
            );
          }
        }

        return updatedTriggers;
      };

      // Calculate the result synchronously
      const newTriggers = calculateUpdatedTriggers(triggers);

      // Update state
      setTriggers(newTriggers);

      // Return the calculated result
      return newTriggers;
    },
    [triggers]
  );

  const isActionChecked = useCallback(
    (contentType, action) => {
      const trigger = triggers.find((t) => t.type === contentType);
      return trigger?.functions.includes(action) ?? false;
    },
    [triggers]
  );

  const resetTriggers = useCallback(() => setTriggers([]), []);

  return {
    triggers,
    setTriggers,
    toggleTriggerAction,
    isActionChecked,
    resetTriggers,
  };
};
